import { useCallback, useMemo, useState } from 'react';

import { Job, JobId } from '~extension/web2print/model/Job';
import { JobActions } from '~extension/web2print/model/JobActions';
import { AssetId } from '~modules/media-library/model/Asset';
import { makeProgramaticallyLinkClick } from '~utils/link';

type UseJobActionsReturnValue = Resolve<
  {
    idClient: number | undefined;
    idWeb2Print: AssetId | undefined;
    downloadJob: Job | undefined;
    myFilesJob: Job | undefined;
    idDelete: number | undefined;
    sapExportJob: Job | undefined;
    message: string | undefined;
  } & {
    closeWeb2Print: () => void;
    closeDownload: () => void;
    closeMyFiles: () => void;
    closeDelete: () => void;
    closeSapExport: () => void;
    closeMessage: () => void;
  } & JobActions
>;

export default function useJobActions(): UseJobActionsReturnValue {
  // Prepare state
  const [idClient, setIdClient] = useState<number>();
  const [idWeb2Print, setIdWeb2Print] = useState<AssetId>();
  const [downloadJob, setDownloadJob] = useState<Job>();
  const [myFilesJob, setMyFilesJob] = useState<Job>();
  const [idDelete, setIdDelete] = useState<number>();
  const [sapExportJob, setSapExportJob] = useState<Job>();
  const [message, setMessage] = useState<string>();

  // Handler: on edit job
  const onEdit = useCallback((jobId: JobId, clientId: number) => {
    const href = '/tools/web2print/editor/' + clientId + '/' + jobId;
    makeProgramaticallyLinkClick({ href });
  }, []);

  const onDuplicate = useCallback((id: AssetId) => {
    setIdWeb2Print(id);
  }, []);
  const closeWeb2Print = useCallback(() => {
    setIdWeb2Print(undefined);
  }, []);

  const onDownload = useCallback((job: Job) => {
    setDownloadJob(job);
  }, []);
  const closeDownload = useCallback(() => {
    setDownloadJob(undefined);
  }, []);

  const onMyFiles = useCallback((job: Job) => {
    setMyFilesJob(job);
  }, []);
  const closeMyFiles = useCallback(() => {
    setMyFilesJob(undefined);
  }, []);

  const onSapExport = useCallback((job: Job) => {
    setSapExportJob(job);
  }, []);
  const closeSapExport = useCallback(() => {
    setSapExportJob(undefined);
  }, []);

  const onDelete = useCallback((documentId: number, clientId: number) => {
    setIdDelete(documentId);
    setIdClient(clientId);
  }, []);
  const closeDelete = useCallback(() => {
    setIdDelete(undefined);
    setIdClient(undefined);
  }, []);

  const onShowMessage = useCallback((message: string) => {
    setMessage(message);
  }, []);
  const closeMessage = useCallback(() => {
    setMessage(undefined);
  }, []);

  return useMemo<UseJobActionsReturnValue>(
    () => ({
      // IDs
      idClient,
      idWeb2Print,
      downloadJob,
      myFilesJob,
      idDelete,
      sapExportJob,
      message,
      // Actions
      onEdit,
      onDuplicate,
      onDownload,
      onMyFiles,
      onDelete,
      onSapExport,
      onShowMessage,
      closeWeb2Print,
      closeDownload,
      closeMyFiles,
      closeDelete,
      closeSapExport,
      closeMessage,
    }),
    [
      idClient,
      idWeb2Print,
      downloadJob,
      myFilesJob,
      idDelete,
      sapExportJob,
      message,
      onEdit,
      onDuplicate,
      onDownload,
      onMyFiles,
      onDelete,
      onSapExport,
      onShowMessage,
      closeWeb2Print,
      closeDownload,
      closeMyFiles,
      closeDelete,
      closeSapExport,
      closeMessage,
    ],
  );
}
